import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  Loader2, Save, RefreshCw, AlertTriangle,
  Zap, GitCompare, Building2,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useOrg } from '@/contexts/OrgContext'
import { useCadence } from '@/contexts/CadenceContext'
import { supabase } from '@/integrations/supabase/client'
import { updateQaSettings } from '@/lib/edge-functions'
import { CircuitBreakerTable, type CircuitBreakerRow } from '@/components/qa/CircuitBreakerTable'

// Defaults for the similarity thresholds when the columns don't exist yet or the
// settings row is missing. Mirror the hardcoded engine values.
const DEFAULT_SIM_LINKEDIN = 0.65
const DEFAULT_SIM_EMAIL = 0.80
const DEFAULT_ANTI_REPEAT = 0.40

interface QaGlobalsBase {
  qa_threshold: number
  qa_max_attempts: number
  qa_min_acceptable_score: number
  qa_dead_band_width: number
  qa_shadow_mode_active: boolean
}

interface SimilarityGlobals extends QaGlobalsBase {
  qa_similarity_linkedin: number
  qa_similarity_email: number
  qa_email_reply_anti_repeat: number
}

interface SimForm {
  qa_similarity_linkedin: string
  qa_similarity_email: string
  qa_email_reply_anti_repeat: string
}

function toSimForm(g: SimilarityGlobals): SimForm {
  return {
    qa_similarity_linkedin: String(g.qa_similarity_linkedin),
    qa_similarity_email: String(g.qa_similarity_email),
    qa_email_reply_anti_repeat: String(g.qa_email_reply_anti_repeat),
  }
}

// ────────────────────────────────────────────────────────────────────────────
// LimitsSection — the "Límites" tab. The circuit breaker is scoped to the
// cadenceId passed from the shell context (no more "guess the active cadence").
// The similarity thresholds stay org-wide (labeled).
// ────────────────────────────────────────────────────────────────────────────
export function LimitsSection({ cadenceId }: { cadenceId: string }) {
  const { orgId } = useOrg()
  const { cadences } = useCadence()
  const queryClient = useQueryClient()

  const cadence = cadences.find((c) => c.id === cadenceId) ?? null

  // ── Circuit breaker rows for THIS cadence ───────────────────────────────────
  const breakersQuery = useQuery({
    queryKey: ['circuit-breakers', orgId, cadenceId],
    enabled: !!orgId && !!cadenceId,
    queryFn: async (): Promise<CircuitBreakerRow[]> => {
      const { data, error } = await supabase
        .from('step_type_circuit_breaker')
        .select('cadence_id, step_type, status, max_regen_rate, max_reject_rate, window_size, window_hours, min_samples_to_trip')
        .eq('org_id', orgId)
        .eq('cadence_id', cadenceId)
        .order('step_type', { ascending: true })
      if (error) throw new Error(error.message)
      return (data ?? []).map((r) => ({
        cadence_id: r.cadence_id as string,
        step_type: r.step_type as string,
        status: (r.status as string) ?? 'closed',
        max_regen_rate: Number(r.max_regen_rate),
        max_reject_rate: Number(r.max_reject_rate),
        window_size: Number(r.window_size),
        window_hours: Number(r.window_hours),
        min_samples_to_trip: Number(r.min_samples_to_trip),
      }))
    },
  })

  // ── Similarity globals (org_chief_settings, org-scoped RLS) ─────────────────
  const simQuery = useQuery({
    queryKey: ['operational-limits-similarity', orgId],
    enabled: !!orgId,
    queryFn: async (): Promise<SimilarityGlobals | null> => {
      const base = 'qa_threshold, qa_max_attempts, qa_min_acceptable_score, qa_dead_band_width, qa_shadow_mode_active'
      const withSim = `${base}, qa_similarity_linkedin, qa_similarity_email, qa_email_reply_anti_repeat`

      let row: Record<string, unknown> | null = null
      let simColumnsPresent = true
      const full = await supabase
        .from('org_chief_settings')
        .select(withSim)
        .eq('org_id', orgId)
        .maybeSingle()
      if (full.error) {
        simColumnsPresent = false
        const baseOnly = await supabase
          .from('org_chief_settings')
          .select(base)
          .eq('org_id', orgId)
          .maybeSingle()
        if (baseOnly.error) throw new Error(baseOnly.error.message)
        row = baseOnly.data as Record<string, unknown> | null
      } else {
        row = full.data as Record<string, unknown> | null
      }

      if (!row) return null
      const num = (v: unknown, fallback: number) =>
        v === null || v === undefined ? fallback : Number(v)
      return {
        qa_threshold: num(row.qa_threshold, 7.0),
        qa_max_attempts: num(row.qa_max_attempts, 5),
        qa_min_acceptable_score: num(row.qa_min_acceptable_score, 4.5),
        qa_dead_band_width: num(row.qa_dead_band_width, 0),
        qa_shadow_mode_active: Boolean(row.qa_shadow_mode_active),
        qa_similarity_linkedin: simColumnsPresent ? num(row.qa_similarity_linkedin, DEFAULT_SIM_LINKEDIN) : DEFAULT_SIM_LINKEDIN,
        qa_similarity_email: simColumnsPresent ? num(row.qa_similarity_email, DEFAULT_SIM_EMAIL) : DEFAULT_SIM_EMAIL,
        qa_email_reply_anti_repeat: simColumnsPresent ? num(row.qa_email_reply_anti_repeat, DEFAULT_ANTI_REPEAT) : DEFAULT_ANTI_REPEAT,
      }
    },
  })

  // ── Similarity form state (hydrated from query; adjust-during-render) ───────
  const [simForm, setSimForm] = useState<SimForm | null>(null)
  const [hydratedFrom, setHydratedFrom] = useState<SimilarityGlobals | null>(null)
  if (simQuery.data && simQuery.data !== hydratedFrom) {
    setHydratedFrom(simQuery.data)
    setSimForm(toSimForm(simQuery.data))
  }

  const simDirty = !!simForm && !!simQuery.data &&
    JSON.stringify(simForm) !== JSON.stringify(toSimForm(simQuery.data))

  const saveSim = useMutation({
    mutationFn: async () => {
      if (!simForm || !orgId || !simQuery.data) throw new Error('Sin datos que guardar')
      const li = Number(simForm.qa_similarity_linkedin)
      const email = Number(simForm.qa_similarity_email)
      const antiRepeat = Number(simForm.qa_email_reply_anti_repeat)

      const inRange = (v: number) => !Number.isNaN(v) && v >= 0 && v <= 1
      if (!inRange(li)) throw new Error('El umbral de LinkedIn debe estar entre 0 y 1')
      if (!inRange(email)) throw new Error('El umbral de email debe estar entre 0 y 1')
      if (!inRange(antiRepeat)) throw new Error('El umbral anti-repetición debe estar entre 0 y 1')

      const base = simQuery.data
      return updateQaSettings({
        operation: 'globals',
        orgId,
        qa_threshold: base.qa_threshold,
        qa_max_attempts: base.qa_max_attempts,
        qa_min_acceptable_score: base.qa_min_acceptable_score,
        qa_dead_band_width: base.qa_dead_band_width,
        qa_shadow_mode_active: base.qa_shadow_mode_active,
        qa_similarity_linkedin: li,
        qa_similarity_email: email,
        qa_email_reply_anti_repeat: antiRepeat,
      })
    },
    onSuccess: () => {
      toast.success('Umbrales de similitud guardados')
      queryClient.invalidateQueries({ queryKey: ['operational-limits-similarity', orgId] })
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : 'No se pudieron guardar los umbrales')
    },
  })

  const refetchAll = () => {
    breakersQuery.refetch()
    simQuery.refetch()
  }
  const anyFetching = breakersQuery.isFetching || simQuery.isFetching

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Los frenos automáticos que protegen el presupuesto y la calidad del outreach: el circuit
          breaker que corta un paso cuando se regenera o rechaza demasiado, y qué tan distinto debe
          ser un mensaje regenerado del anterior para aceptarlo.
        </p>
        <Button variant="outline" size="sm" onClick={refetchAll} disabled={anyFetching}>
          <RefreshCw className={`h-4 w-4 mr-1 ${anyFetching ? 'animate-spin' : ''}`} />
          Actualizar
        </Button>
      </div>

      {/* ── Circuit breaker (this cadence) ─────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Zap className="h-5 w-5" />
            Circuit breaker por step
          </CardTitle>
          <CardDescription className="flex flex-wrap items-center gap-2">
            <span>Umbrales por paso de esta cadencia.</span>
            {cadence && (
              <Badge variant="outline" className="font-normal">
                {cadence.name}
                <span className="ml-1.5 text-[10px] uppercase text-muted-foreground">
                  {cadence.status}
                </span>
              </Badge>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {breakersQuery.isLoading ? (
            <div className="py-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando circuit breakers…
            </div>
          ) : breakersQuery.error ? (
            <div className="py-8 text-center text-sm">
              <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> No se pudieron leer los circuit breakers.
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => breakersQuery.refetch()}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reintentar
              </Button>
            </div>
          ) : orgId ? (
            <CircuitBreakerTable
              rows={breakersQuery.data ?? []}
              steps={cadence?.steps ?? []}
              orgId={orgId}
              cadenceId={cadenceId}
            />
          ) : null}
        </CardContent>
      </Card>

      {/* ── Similarity thresholds (org-wide) ───────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <GitCompare className="h-5 w-5" />
            Umbrales de similitud
            <Badge variant="secondary" className="ml-1 gap-1 font-normal">
              <Building2 className="h-3 w-3" /> aplica a toda la org
            </Badge>
          </CardTitle>
          <CardDescription>
            Qué tan distinto debe ser un mensaje regenerado del anterior para aceptarlo. Más alto =
            se exige menos diferencia (más tolerante a repetir); más bajo = se exige más variación.
            Son globales para toda la organización.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          {simQuery.isLoading ? (
            <div className="py-10 flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando umbrales…
            </div>
          ) : simQuery.error ? (
            <div className="py-8 text-center text-sm">
              <p className="text-red-600 dark:text-red-400 flex items-center justify-center gap-1.5">
                <AlertTriangle className="h-4 w-4" /> No se pudieron leer los umbrales de similitud.
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => simQuery.refetch()}>
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reintentar
              </Button>
            </div>
          ) : (
            <>
              {!simForm ? (
                <p className="text-sm text-muted-foreground">
                  Esta organización aún no tiene ajustes de QA guardados. Guarda una vez para crearlos.
                </p>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <SimField
                  label="Similitud LinkedIn"
                  hint="Umbral (0–1) de parecido entre un DM/invitación regenerado y el anterior. Por encima de esto se considera 'muy parecido' y se rechaza. Default 0.65."
                  value={simForm?.qa_similarity_linkedin ?? ''}
                  onChange={(v) => setSimForm((s) => s && { ...s, qa_similarity_linkedin: v })}
                />
                <SimField
                  label="Similitud email"
                  hint="Umbral (0–1) de parecido entre un email regenerado y el anterior. Los emails toleran más parecido que LinkedIn. Default 0.80."
                  value={simForm?.qa_similarity_email ?? ''}
                  onChange={(v) => setSimForm((s) => s && { ...s, qa_similarity_email: v })}
                />
                <SimField
                  label="Anti-repetición reply"
                  hint="Umbral (0–1) de parecido entre el reply de seguimiento y el email original del mismo hilo. Bajo = se exige que el follow-up diga algo nuevo. Default 0.40."
                  value={simForm?.qa_email_reply_anti_repeat ?? ''}
                  onChange={(v) => setSimForm((s) => s && { ...s, qa_email_reply_anti_repeat: v })}
                />
              </div>

              <div className="flex items-center gap-3">
                <Button
                  disabled={!simDirty || saveSim.isPending || !simForm}
                  onClick={() => saveSim.mutate()}
                >
                  {saveSim.isPending
                    ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    : <Save className="h-4 w-4 mr-2" />}
                  Guardar umbrales
                </Button>
                {simDirty && (
                  <span className="text-xs text-muted-foreground">Hay cambios sin guardar</span>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

function SimField({
  label, hint, value, onChange,
}: {
  label: string
  hint: string
  value: string | number
  onChange: (v: string) => void
}) {
  return (
    <div className="space-y-1">
      <Label className="text-sm">{label}</Label>
      <Input
        type="number"
        step="0.05"
        min={0}
        max={1}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9"
      />
      <p className="text-[11px] text-muted-foreground leading-tight">{hint}</p>
    </div>
  )
}
